/**
 * Page 2: the full news feed.
 *
 * The main trial page, reached from the email's primary CTA and from every
 * "Back to my news feed". Everything on a row comes from the frozen edition, so
 * an old email opens the list it was sent with.
 */

import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import TrialShell from './TrialShell';
import TrialExpired from './TrialExpired';
import trialApi from './trialApi';
import { relativeTime, sourceLineParts, useFocusHeading, useTrialData } from './useTrialPage';
import { EVENTS, track } from '../../analytics/campaignAnalytics';

function FeedbackButtons({ value, pending, onChange }) {
  const set = (next) => onChange(value === next ? null : next);
  return (
    <div className="tr-feedback">
      <button
        type="button"
        className="tr-fb-btn tr-fb-btn--yes"
        aria-pressed={value === 'relevant'}
        aria-label="Mark this story relevant"
        disabled={pending}
        onClick={() => set('relevant')}
      >
        <span aria-hidden="true">&#128077;</span> Relevant
      </button>
      <button
        type="button"
        className="tr-fb-btn tr-fb-btn--no"
        aria-pressed={value === 'not_relevant'}
        aria-label="Mark this story not relevant"
        disabled={pending}
        onClick={() => set('not_relevant')}
      >
        <span aria-hidden="true">&#128078;</span> Not relevant
      </button>
      {/* "Saved" appears only once a choice is actually stored, which is the
          whole reason the control ships at all. */}
      {value && !pending && <span className="tr-fb-saved" role="status">Saved</span>}
    </div>
  );
}

function StoryRow({ token, story, onFeedback }) {
  const [pending, setPending] = useState(false);
  const line = sourceLineParts(story);
  const href = `/trial/feed/${token}/story/${story.storyId}`;

  const change = async (next) => {
    setPending(true);
    try {
      await onFeedback(story.storyId, next);
    } finally {
      setPending(false);
    }
  };

  return (
    <article className={`tr-story-row ${story.feedback === 'not_relevant' ? 'tr-story-row--muted' : ''}`}>
      <div className="tr-story-top">
        {story.category && <span className="tr-tag">{story.category}</span>}
        {/*
          * Labelled, not a bare timestamp. `publishedAt` is when the cluster's
          * coverage STARTED, so an unlabelled "7 days ago" beside a headline
          * reads as "this story is stale" when it may have been updated this
          * morning. Naming it matches the story page, which already says
          * "First reported".
          */}
        {relativeTime(story.publishedAt) && (
          <span className="tr-meta">{`First reported ${relativeTime(story.publishedAt)}`}</span>
        )}
      </div>
      <Link className="tr-story-headline" to={href}>{story.headline}</Link>
      <p className="tr-sources-line">
        <span className="tr-sources-count">{line.count}</span>
        {line.names ? ` · ${line.names}` : ''}
      </p>
      <div className="tr-story-actions">
        <Link className="tr-link" to={href}>View story &amp; sources &#8594;</Link>
        <FeedbackButtons value={story.feedback} pending={pending} onChange={change} />
      </div>
    </article>
  );
}

export default function FeedPage() {
  const { token } = useParams();
  const { data, error, loading } = useTrialData(
    (cfg) => trialApi.getFeed(token, cfg),
    [token],
  );
  const [stories, setStories] = useState(null);
  const heading = useFocusHeading(Boolean(data));

  // One page-load event, not a click event. The handoff is explicit that a
  // click does not prove the destination rendered.
  React.useEffect(() => {
    if (data) {
      track(EVENTS.FULL_FEED_OPENED, {
        edition_id: data.edition?.id,
        story_count: data.edition?.storyCount,
        is_welcome: data.edition?.isWelcome,
      });
      setStories(data.stories);
    }
  }, [data]);

  const onFeedback = useCallback(async (storyId, value) => {
    // Optimistic, then reconciled. A verdict that visibly lags the tap reads as
    // broken, and the server's answer is authoritative either way.
    setStories((cur) => cur.map((s) => (s.storyId === storyId ? { ...s, feedback: value } : s)));
    try {
      const res = await trialApi.setFeedback(token, storyId, value);
      setStories((cur) => cur.map((s) => (s.storyId === storyId ? { ...s, feedback: res.feedback } : s)));
      track(EVENTS.STORY_FEEDBACK_SUBMITTED, { story_id: storyId, value: value || 'cleared' });
    } catch {
      // Put it back rather than leaving a choice that looks saved and is not.
      setStories((cur) => cur.map((s) => (s.storyId === storyId ? { ...s, feedback: null } : s)));
    }
  }, [token]);

  if (error?.code === 'TRIAL_LINK_EXPIRED' || error?.code === 'TRIAL_LINK_INVALID') {
    return <TrialExpired code={error.code} />;
  }

  return (
    <TrialShell
      token={token}
      active="feed"
      onContactClick={() => track(EVENTS.CONTACT_CLICKED)}
      onSiteClick={() => track(EVENTS.SITE_LINK_CLICKED)}
    >
      <main className="tr-main">
        <div className="tr-wrap tr-wrap--feed">
          {loading && <p className="tr-body" role="status">Loading your feed</p>}
          {error && !loading && <p className="tr-error" role="alert">{error.message}</p>}

          {data && (
            <>
              <header style={{ marginBottom: 24 }}>
                <h1 className="tr-h1" tabIndex={-1} ref={heading}>Your personalized news feed</h1>
                <p className="tr-lede">
                  Stories selected for your interests, gathered from news sources around the world.
                </p>
                <p className="tr-meta" style={{ marginTop: 12 }}>
                  {`${data.edition.storyCount} ${data.edition.storyCount === 1 ? 'story' : 'stories'}`}
                  {data.topics?.length ? ` · Your topics: ${data.topics.join(', ')}` : ''}
                </p>
                <div style={{ marginTop: 20 }}>
                  <Link className="tr-btn tr-btn--primary tr-btn--block-mobile" to={`/trial/feed/${token}/refine`}>
                    Refine your feed
                  </Link>
                </div>
              </header>

              {stories?.length ? (
                <div className="tr-panel tr-panel--flush">
                  {stories.map((s) => (
                    <StoryRow key={s.storyId} token={token} story={s} onFeedback={onFeedback} />
                  ))}
                </div>
              ) : (
                /* No placeholders and no empty list: the designed empty state,
                   which keeps a working next step. */
                <div className="tr-notice">
                  <h2 className="tr-h2">No matching stories yet</h2>
                  <p className="tr-body" style={{ marginTop: 8 }}>
                    We haven&rsquo;t found stories for your current topics today. Adding a broader
                    topic usually helps.
                  </p>
                  <Link className="tr-btn tr-btn--primary" to={`/trial/feed/${token}/refine`} style={{ marginTop: 16 }}>
                    Refine your feed
                  </Link>
                </div>
              )}

              {/*
                * `hasMore` is false until pagination exists on the backend, and
                * the handoff forbids a Find more button that cannot load
                * anything. So this is the "all caught up" block rather than a
                * disabled button.
                */}
              {stories?.length > 0 && !data.hasMore && (
                <div className="tr-notice" style={{ marginTop: 24, textAlign: 'center' }}>
                  <p className="tr-body"><span aria-hidden="true">&#10003;</span> You&rsquo;re all caught up for now.</p>
                  <p className="tr-meta" style={{ marginTop: 4 }}>
                    We&rsquo;ll continue looking for relevant stories based on your interests.
                  </p>
                  <Link className="tr-link" to={`/trial/feed/${token}/refine`} style={{ display: 'inline-block', marginTop: 12 }}>
                    Refine your feed
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </TrialShell>
  );
}
